/**
 * MSW Overrides: Availability Browse (multi-city) scenario
 *
 * Intercepts GET /availability and returns rich availability data spread across
 * all dates in the requested window (3 flights/day × 7 days = 21 trips).
 * The origin/destination pair is matched against CITY_GROUPS; unknown pairs
 * fall back to a sensible default so any route renders data.
 *
 * The number of active city groups is controlled by CITY_GROUP_COUNT in the factory.
 *
 * Enable by setting ENABLE_MSW=true and NUXT_PUBLIC_MSW_OVERRIDES_AVAILABILITY_BROWSE=true
 * in your .env, then restart the dev server.
 */

import type { HttpHandler } from 'msw';
import { http, HttpResponse } from 'msw';
import { BFF_ENDPOINTS, createMockAvailabilityBrowseResponse, toMSWPattern } from '@cvo/domain';

function enumerateDates(from: string | null, to: string | null): string[] {
  if (!from || !to) return from ? [from] : [];

  const dates: string[] = [];
  const current = new Date(`${from}T00:00:00Z`);
  const end = new Date(`${to}T00:00:00Z`);
  while (current <= end) {
    dates.push(current.toISOString().slice(0, 10));
    current.setUTCDate(current.getUTCDate() + 1);
  }
  return dates;
}

export function createAvailabilityBrowseOverrides(baseUrl: string): HttpHandler[] {
  const sanitizedBaseUrl = baseUrl.replace(/\/$/, '');

  return [
    http.get(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.TRIP_SEARCH)}`,
      ({ request }) => {
        const url = new URL(request.url);
        const origin = url.searchParams.get('origin') ?? 'MAD';
        const destination = url.searchParams.get('destination') ?? 'BCN';
        const from = url.searchParams.get('from');
        const to = url.searchParams.get('to');

        const dates = enumerateDates(from, to);
        const response = createMockAvailabilityBrowseResponse(origin, destination, dates);
        return HttpResponse.json(response);
      },
    ),
  ];
}
